import { Icon, Skeleton, TooltipProvider, cn } from "@nexus/ui";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { ColumnResizeHandle } from "@/components/data-table/ColumnResizeHandle";
import { humanizePersonaTitle } from "@/features/personas/lib/persona-format";

import { PersonaOpportunitySelectCheckbox } from "../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";

import {
  isBareNoPersonaList,
  isNoPersonaGroup,
  type OpportunityHubGroup,
} from "./hub-chrome-grouping.js";
import type { HubChromeOpportunityRow, HubSetsById } from "./hub-chrome.types.js";
import type { HubOpportunityColumn } from "./hub-opportunity-columns.js";
import {
  HUB_KEYBOARD_RESIZE_STEP_PX,
  HUB_SELECT_COLUMN_WIDTH_PX,
  clampHubColumnWidth,
  hubColumnWidthStorage,
  hubOpportunityTableWidthPx,
  readHubColumnWidths,
  resolveHubColumnWidth,
  writeHubColumnWidths,
  type HubColumnWidthScope,
} from "./hub-column-widths.js";
import { HUB_OPPORTUNITY_SELECTION_BAR_SCROLL_PADDING } from "./OpportunityHubActionBar.js";
import { OpportunityHubGroupFold } from "./OpportunityHubGroupFold.js";
import { OpportunityHubGroupFooter } from "./OpportunityHubGroupFooter.js";
import type { HubOpportunityVirtualItem } from "./hub-opportunity-virtual-items.js";
import type { HubOpportunitySort } from "./hub-opportunity-table.js";
import { handleRowActivationKey } from "./hub-row-activation.js";
import { hubOpportunitySelectionEnabled } from "./hub-fanout.js";
import { useOpportunityVirtualWindow } from "./useOpportunityVirtualWindow.js";

const EMPTY_KEYS: ReadonlySet<string> = new Set();
const EMPTY_HAS_MORE: Readonly<Record<string, boolean>> = {};

const WORKSPACE_HEAD_CELL =
  "whitespace-nowrap px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
const WORKSPACE_BODY_CELL = "px-3 py-3 align-middle text-[13px] text-foreground";
const SORTED_COLUMN_TINT = "bg-primary/5";
/** Space between group cards; padding rather than margin so the virtualizer measures it. */
const GROUP_GAP = "pt-4";
/** Side borders that carry an expanded group's card from its header down to its footer. */
const CARD_SIDES = "border-x border-border";
/**
 * Each virtualized chunk is its own table. The width has to be a shared pixel
 * length: a percentage on a fixed table inside the max-content grid resolves
 * cyclically and Chrome blows the columns out to ~1,000,000px.
 */

export interface OpportunityHubOpportunitiesTableProps {
  groups: OpportunityHubGroup[];
  personaColumns: HubOpportunityColumn[];
  legacyColumns: HubOpportunityColumn[];
  /** Winning-ad columns. Used when every row in the block is Post-Flight. */
  postFlightColumns?: HubOpportunityColumn[];
  sort: HubOpportunitySort | null;
  onSortChange: (columnId: string) => void;
  expandedGroupKeys: ReadonlySet<string>;
  onToggleGroupExpanded: (key: string) => void;
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (opportunityId: string) => void;
  /** Header checkbox: selects every loaded row of the group, or clears them when all are selected. */
  onToggleMembers?: (opportunityIds: string[]) => void;
  onView: (row: HubChromeOpportunityRow) => void;
  /** Paging status rendered inside the scroller so its sentinel can observe. */
  footer?: React.ReactNode;
  loadingKeys?: ReadonlySet<string>;
  hasMoreByKey?: Readonly<Record<string, boolean>>;
  isLoadingMoreGroups?: boolean;
  hasMoreGroups?: boolean;
  onLoadMoreGroups?: () => void;
  onLoadMoreGroup?: (groupKey: string) => void;
  /** Visible groups that need their first child page. */
  onGroupsNeeded?: (groupKeys: readonly string[]) => void;
  /** Bumped when the opportunity scope resets so child rows are requested again. */
  groupLoadGeneration?: number;
  /** Names the set on each fold when more than one set is selected. */
  showSetBadge?: boolean;
  setsById?: HubSetsById;
}

function HeadCell({
  columnId,
  label,
  isNumeric,
  isSortable,
  sort,
  onSortChange,
  widthPx,
  resizeLabel,
  resizeValueNow,
  resizeValueMin,
  resizeValueMax,
  isResizing = false,
  onResizePointerDown,
  onResizeTouchStart,
  onResizeDoubleClick,
  onResizeKeyDown,
}: {
  columnId: string;
  label: string;
  isNumeric?: boolean;
  isSortable: boolean;
  sort: HubOpportunitySort | null;
  onSortChange: (columnId: string) => void;
  widthPx: number;
  resizeLabel?: string;
  resizeValueNow?: number;
  resizeValueMin?: number;
  resizeValueMax?: number;
  isResizing?: boolean;
  onResizePointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onResizeTouchStart?: (event: React.TouchEvent<HTMLDivElement>) => void;
  onResizeDoubleClick?: () => void;
  onResizeKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}) {
  const sorted = sort?.columnId === columnId ? sort.direction : null;
  const sortIcon = sorted === "asc" ? "arrow-up" : sorted === "desc" ? "arrow-down" : "chevrons-up-down";
  const labelId = React.useId();

  return (
    <th
      scope="col"
      style={{ width: widthPx }}
      aria-labelledby={labelId}
      aria-sort={
        !isSortable ? undefined : sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"
      }
      className={cn(
        WORKSPACE_HEAD_CELL,
        "relative group/head",
        isSortable && "cursor-pointer select-none",
        sorted && "font-semibold text-primary",
      )}
      onClick={
        isSortable
          ? (event) => {
              if ((event.target as Element).closest("[data-resize-handle]")) return;
              onSortChange(columnId);
            }
          : undefined
      }
    >
      <span className={cn("inline-flex items-center gap-1", isNumeric && "w-full justify-end")}>
        <span id={labelId} className="truncate">{label}</span>
        {isSortable ? (
          <Icon
            name={sortIcon}
            size="xs"
            className={cn(
              "shrink-0 transition-opacity",
              sorted
                ? "text-primary opacity-100"
                : "text-muted-foreground/40 opacity-0 group-hover/head:opacity-60",
            )}
          />
        ) : null}
      </span>
      {resizeLabel != null &&
      onResizePointerDown != null &&
      onResizeTouchStart != null &&
      onResizeDoubleClick != null &&
      onResizeKeyDown != null ? (
        <ColumnResizeHandle
          label={resizeLabel}
          valueNow={resizeValueNow}
          valueMin={resizeValueMin}
          valueMax={resizeValueMax}
          isActive={isResizing}
          onPointerDown={onResizePointerDown}
          onTouchStart={onResizeTouchStart}
          onDoubleClick={onResizeDoubleClick}
          onKeyDown={onResizeKeyDown}
        />
      ) : null}
    </th>
  );
}

/** Row markup copied from the single-set workspace table so both tables read identically. */
function PersonaDataRow({
  row,
  columns,
  label,
  selected,
  sortedColumnId,
  selectionEnabled,
  onToggleSelect,
  onView,
  index,
  measureRef,
  widthFor,
}: {
  row: HubChromeOpportunityRow;
  columns: HubOpportunityColumn[];
  label: string;
  selected: boolean;
  sortedColumnId: string | null;
  selectionEnabled: boolean;
  onToggleSelect: () => void;
  onView: () => void;
  index: number;
  measureRef: (element: Element | null) => void;
  widthFor: (columnId: string) => number;
}) {
  const { t } = useTranslation();

  return (
    <tr
      className="group/row cursor-pointer border-b border-border/70 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none data-[state=selected]:bg-primary/[0.03]"
      data-index={index}
      ref={measureRef}
      data-state={selected ? "selected" : undefined}
      tabIndex={0}
      onClick={onView}
      onKeyDown={(event) => handleRowActivationKey(event, onView)}
    >
      <td className={WORKSPACE_BODY_CELL} style={{ width: HUB_SELECT_COLUMN_WIDTH_PX }}>
        {selectionEnabled ? (
          <PersonaOpportunitySelectCheckbox
            checked={selected}
            onToggle={onToggleSelect}
            label={t("personaOpportunities.hub.opportunities.selectTopicAria", {
              title: label,
              defaultValue: `Select ${label}`,
            })}
          />
        ) : (
          <span className="sr-only">
            {t("personaOpportunities.active.selectColumn", "Select")}
          </span>
        )}
      </td>
      {columns.map((column) => {
        const extra =
          typeof column.cellClassName === "function"
            ? column.cellClassName(row)
            : column.cellClassName;
        const content = column.cell(row);
        const widthPx = widthFor(column.id);
        return (
          <td
            key={column.id}
            style={{ width: widthPx, maxWidth: widthPx }}
            className={cn(
              WORKSPACE_BODY_CELL,
              extra,
              column.id === sortedColumnId && SORTED_COLUMN_TINT,
            )}
          >
            {content}
          </td>
        );
      })}
    </tr>
  );
}

type TableBlock =
  | { kind: "fold"; key: string; group: OpportunityHubGroup; index: number }
  | {
      kind: "table";
      key: string;
      groupKey: string;
      legacy: boolean;
      rows: Extract<HubOpportunityVirtualItem, { kind: "row" }>[];
      indexes: number[];
    }
  | { kind: "skeleton"; key: string; index: number; groupKey: string | null }
  | {
      kind: "footer";
      key: string;
      index: number;
      item: Extract<HubOpportunityVirtualItem, { kind: "groupFooter" }>;
    };

function tableBlocks(
  items: readonly HubOpportunityVirtualItem[],
  indexes: readonly number[],
): TableBlock[] {
  const blocks: TableBlock[] = [];
  for (const index of indexes) {
    const item = items[index];
    if (!item) continue;
    if (item.kind === "group") {
      blocks.push({ kind: "fold", key: item.key, group: item.group, index });
      continue;
    }
    if (item.kind === "skeleton") {
      blocks.push({ kind: "skeleton", key: item.key, index, groupKey: item.groupKey });
      continue;
    }
    if (item.kind === "groupFooter") {
      blocks.push({ kind: "footer", key: item.key, index, item });
      continue;
    }
    if (item.kind !== "row") continue;
    const last = blocks[blocks.length - 1];
    if (last?.kind === "table" && last.groupKey === item.groupKey) {
      last.rows.push(item);
      last.indexes.push(index);
    } else {
      blocks.push({
        kind: "table",
        key: item.key,
        groupKey: item.groupKey,
        legacy: isNoPersonaGroup({ key: item.groupKey }),
        rows: [item],
        indexes: [index],
      });
    }
  }
  return blocks;
}

function selectedCountIn(rows: readonly HubChromeOpportunityRow[], selectedIds: ReadonlySet<string>) {
  return rows.reduce((total, row) => total + (selectedIds.has(row.id) ? 1 : 0), 0);
}

function headerCheckboxState(selected: number, total: number): boolean | "indeterminate" {
  if (selected === 0 || total === 0) return false;
  return selected === total ? true : "indeterminate";
}

/**
 * Folds sit outside their table. The list itself scrolls sideways so the
 * horizontal scrollbar stays at the viewport bottom; folds stay pinned left.
 * The first column is the row checkbox, matching the workspace and legacy tables.
 */
export function OpportunityHubOpportunitiesTable({
  groups,
  personaColumns,
  legacyColumns,
  postFlightColumns = [],
  sort,
  onSortChange,
  expandedGroupKeys,
  onToggleGroupExpanded,
  selectedIds,
  onToggleSelect,
  onToggleMembers,
  onView,
  footer,
  loadingKeys = EMPTY_KEYS,
  hasMoreByKey = EMPTY_HAS_MORE,
  isLoadingMoreGroups = false,
  hasMoreGroups = false,
  onLoadMoreGroups,
  onLoadMoreGroup,
  onGroupsNeeded,
  groupLoadGeneration = 0,
  showSetBadge = false,
  setsById,
}: OpportunityHubOpportunitiesTableProps) {
  const { t } = useTranslation();
  const [widthsByScope, setWidthsByScope] = React.useState(() => {
    const storage = hubColumnWidthStorage();
    if (storage == null) return { persona: {}, legacy: {} };
    return {
      persona: readHubColumnWidths(storage, "persona", personaColumns),
      legacy: readHubColumnWidths(storage, "legacy", legacyColumns),
    };
  });
  const [resizingColumnId, setResizingColumnId] = React.useState<string | null>(null);
  const dragCleanupRef = React.useRef<(() => void) | null>(null);
  React.useEffect(() => () => dragCleanupRef.current?.(), []);
  React.useEffect(() => {
    const storage = hubColumnWidthStorage();
    if (storage == null) return;
    writeHubColumnWidths(storage, "persona", widthsByScope.persona);
  }, [widthsByScope.persona]);
  React.useEffect(() => {
    const storage = hubColumnWidthStorage();
    if (storage == null) return;
    writeHubColumnWidths(storage, "legacy", widthsByScope.legacy);
  }, [widthsByScope.legacy]);

  function widthFor(scope: HubColumnWidthScope, column: HubOpportunityColumn) {
    return resolveHubColumnWidth(column, widthsByScope[scope]);
  }

  function setColumnWidth(scope: HubColumnWidthScope, columnId: string, width: number | null) {
    setWidthsByScope((current) => {
      const nextScope = { ...current[scope] };
      if (width == null) delete nextScope[columnId];
      else nextScope[columnId] = width;
      return { ...current, [scope]: nextScope };
    });
  }

  function beginResize(
    scope: HubColumnWidthScope,
    column: HubOpportunityColumn,
    event: React.PointerEvent<HTMLDivElement>,
  ) {
    dragCleanupRef.current?.();
    const pointerId = event.pointerId;
    const originX = event.clientX;
    const handle = event.currentTarget;
    const startWidth = resolveHubColumnWidth(column, widthsByScope[scope]);
    setResizingColumnId(column.id);
    if (typeof handle.setPointerCapture === "function") {
      try {
        handle.setPointerCapture(pointerId);
      } catch {
        // The pointer can end before the browser accepts capture.
      }
    }

    let isFinished = false;

    function isThisPointer(incoming: PointerEvent) {
      return incoming.pointerId === pointerId;
    }

    function detach() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      handle.removeEventListener("lostpointercapture", onLostCapture);
    }

    function finish() {
      if (isFinished) return;
      isFinished = true;
      setResizingColumnId(null);
      // lostpointercapture does not bubble, and releasing capture fires it. Detach first so finish runs once.
      detach();
      if (typeof handle.hasPointerCapture === "function" && handle.hasPointerCapture(pointerId)) {
        try {
          handle.releasePointerCapture(pointerId);
        } catch {
          // Capture was already released.
        }
      }
      if (dragCleanupRef.current === finish) dragCleanupRef.current = null;
    }

    function onMove(incoming: PointerEvent) {
      if (!isThisPointer(incoming)) return;
      setColumnWidth(
        scope,
        column.id,
        clampHubColumnWidth(startWidth + incoming.clientX - originX, column),
      );
    }

    function onUp(incoming: PointerEvent) {
      if (!isThisPointer(incoming)) return;
      finish();
    }

    function onCancel(incoming: PointerEvent) {
      if (!isThisPointer(incoming)) return;
      finish();
    }

    function onLostCapture(incoming: PointerEvent) {
      if (!isThisPointer(incoming)) return;
      finish();
    }

    dragCleanupRef.current = finish;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    handle.addEventListener("lostpointercapture", onLostCapture);
  }

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const sync = () => setFrameWidth(el.clientWidth);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const { items, rendered, topPad, bottomPad, measureElement } = useOpportunityVirtualWindow({
    groups,
    expandedGroupKeys,
    loadingKeys,
    hasMoreByKey,
    isLoadingMoreGroups,
    hasMoreGroups,
    scrollRef,
    onLoadMoreGroups,
    onLoadMoreGroup,
    onGroupsNeeded,
    groupLoadGeneration,
    groupFooters: true,
  });
  const blocks = tableBlocks(items, rendered);
  const isBareTable = isBareNoPersonaList(groups);
  const groupsByKey = React.useMemo(
    () => new Map(groups.map((group) => [group.key, group])),
    [groups],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div
        ref={scrollRef}
        data-opportunity-hub-table
        className={cn(
          "min-h-0 flex-initial overflow-auto",
          isBareTable && "rounded-md border border-border",
          selectedIds.size > 0 && HUB_OPPORTUNITY_SELECTION_BAR_SCROLL_PADDING,
        )}
      >
        {topPad > 0 ? <div aria-hidden style={{ height: topPad }} /> : null}
        {blocks.map((block) => {
          if (block.kind === "fold") {
            return (
              <div
                key={block.key}
                data-index={block.index}
                ref={measureElement}
                className={cn("sticky left-0", block.index > 0 && GROUP_GAP)}
              >
                <OpportunityHubGroupFold
                  group={block.group}
                  expanded={expandedGroupKeys.has(block.group.key)}
                  onToggle={() => onToggleGroupExpanded(block.group.key)}
                  selectedCount={selectedCountIn(block.group.rows, selectedIds)}
                  showSetBadge={showSetBadge}
                  setsById={setsById}
                />
              </div>
            );
          }
          if (block.kind === "skeleton") {
            const closesCard = block.groupKey != null && !isBareTable;
            return (
              <div
                key={block.key}
                role="status"
                data-index={block.index}
                ref={measureElement}
                className={cn(
                  "sticky left-0",
                  closesCard ? cn(CARD_SIDES, "rounded-b-md border-b px-3 py-2") : GROUP_GAP,
                )}
              >
                <Skeleton className="h-8 w-full" />
                <span className="sr-only">{t("common.loading")}</span>
              </div>
            );
          }
          if (block.kind === "footer") {
            return (
              <div
                key={block.key}
                data-index={block.index}
                ref={measureElement}
                className={cn(
                  "sticky left-0",
                  isBareTable
                    ? "border-t border-border"
                    : "rounded-b-md border border-border",
                )}
              >
                <OpportunityHubGroupFooter
                  shownCount={block.item.shownCount}
                  totalCount={block.item.totalCount}
                  hasMore={block.item.hasMore}
                  isLoading={block.item.isLoading}
                  isLegacy={isNoPersonaGroup({ key: block.item.groupKey })}
                  onLoadMore={
                    onLoadMoreGroup ? () => onLoadMoreGroup(block.item.groupKey) : undefined
                  }
                />
              </div>
            );
          }
          const blockRows = block.rows.map((item) => item.row);
          const isPostFlightBlock =
            postFlightColumns.length > 0 &&
            blockRows.length > 0 &&
            blockRows.every((row) => row.setType === "POST_FLIGHT");
          const columns = isPostFlightBlock
            ? postFlightColumns
            : block.legacy
              ? legacyColumns
              : personaColumns;
          const scope: HubColumnWidthScope = block.legacy ? "legacy" : "persona";
          const tableWidthPx = hubOpportunityTableWidthPx(columns, widthsByScope[scope]);
          const groupRows = groupsByKey.get(block.groupKey)?.rows ?? [];
          const groupSelectedCount = selectedCountIn(groupRows, selectedIds);
          const groupTitle = isNoPersonaGroup({ key: block.groupKey })
            ? t("personaOpportunities.hub.groupBy.noPersona", "No persona")
            : humanizePersonaTitle(groupsByKey.get(block.groupKey)?.title ?? "");
          return (
            <div key={block.key} className="grid w-max min-w-full overflow-clip">
              {/* Viewport-width frame: the sheet is wider than the scroller, so a border
                  on the sheet itself leaves the right edge off-screen. */}
              {!isBareTable ? (
                <div
                  aria-hidden
                  className={cn(
                    "pointer-events-none sticky left-0 z-20 col-start-1 row-start-1 self-stretch",
                    CARD_SIDES,
                  )}
                  style={{ width: frameWidth > 0 ? frameWidth : "100%" }}
                />
              ) : null}
              <table
                className="col-start-1 row-start-1 table-fixed border-collapse"
                style={{ width: tableWidthPx }}
              >
                <thead className="sticky top-0 z-10 bg-card">
                  <tr className="border-b border-border bg-muted/30">
                    <th
                      className={cn(WORKSPACE_HEAD_CELL, "w-10")}
                      scope="col"
                      style={{ width: HUB_SELECT_COLUMN_WIDTH_PX }}
                    >
                      {onToggleMembers && !isPostFlightBlock ? (
                        <PersonaOpportunitySelectCheckbox
                          checked={headerCheckboxState(groupSelectedCount, groupRows.length)}
                          onToggle={() =>
                            onToggleMembers(
                              groupRows
                                .filter(hubOpportunitySelectionEnabled)
                                .map((row) => row.id),
                            )
                          }
                          label={t("personaOpportunities.hub.groupBy.selectGroupAria", {
                            title: groupTitle,
                            defaultValue: "Select all in {{title}}",
                          })}
                        />
                      ) : (
                        <span className="sr-only">
                          {t("personaOpportunities.active.selectColumn", "Select")}
                        </span>
                      )}
                    </th>
                    {columns.map((column) => (
                      <HeadCell
                        key={column.id}
                        columnId={column.id}
                        label={column.header}
                        isNumeric={column.isNumeric}
                        isSortable={column.sortValue != null}
                        sort={sort}
                        onSortChange={onSortChange}
                        widthPx={widthFor(scope, column)}
                        resizeLabel={
                          column.enableResizing
                            ? t("opportunities.table.resizeColumnNamed", {
                                column: column.header,
                                defaultValue: "Resize {{column}}",
                              })
                            : undefined
                        }
                        resizeValueNow={column.enableResizing ? widthFor(scope, column) : undefined}
                        resizeValueMin={column.enableResizing ? column.minSize : undefined}
                        resizeValueMax={column.enableResizing ? column.maxSize : undefined}
                        isResizing={resizingColumnId === column.id}
                        onResizePointerDown={
                          column.enableResizing
                            ? (event) => {
                                event.preventDefault();
                                beginResize(scope, column, event);
                              }
                            : undefined
                        }
                        onResizeTouchStart={column.enableResizing ? () => undefined : undefined}
                        onResizeDoubleClick={
                          column.enableResizing
                            ? () => setColumnWidth(scope, column.id, null)
                            : undefined
                        }
                        onResizeKeyDown={
                          column.enableResizing
                            ? (event) => {
                                if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
                                event.preventDefault();
                                const delta =
                                  event.key === "ArrowRight"
                                    ? HUB_KEYBOARD_RESIZE_STEP_PX
                                    : -HUB_KEYBOARD_RESIZE_STEP_PX;
                                const next = clampHubColumnWidth(
                                  widthFor(scope, column) + delta,
                                  column,
                                );
                                setColumnWidth(scope, column.id, next);
                              }
                            : undefined
                        }
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((item, rowIndex) => (
                    <PersonaDataRow
                      key={item.key}
                      row={item.row}
                      columns={columns}
                      label={
                        isPostFlightBlock || !block.legacy
                          ? item.row.angle
                          : (item.row.topicTitle ?? item.row.angle)
                      }
                      selected={selectedIds.has(item.row.id)}
                      sortedColumnId={sort?.columnId ?? null}
                      selectionEnabled={hubOpportunitySelectionEnabled(item.row)}
                      onToggleSelect={() => onToggleSelect(item.row.id)}
                      onView={() => onView(item.row)}
                      index={block.indexes[rowIndex] ?? 0}
                      measureRef={measureElement}
                      widthFor={(columnId) => {
                        const column = columns.find((item) => item.id === columnId);
                        return column ? widthFor(scope, column) : 0;
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
        {bottomPad > 0 ? <div aria-hidden style={{ height: bottomPad }} /> : null}
        {footer ? (
          <div className={cn("sticky left-0", blocks.length > 0 && GROUP_GAP)}>{footer}</div>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
